import { useState, ChangeEvent, FormEvent, useMemo } from "react";
import { useDispatch } from "react-redux";
import Alert from "react-bootstrap/Alert";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { nanoid } from 'nanoid'
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { createOrganizationReduxRequest } from "@redux-requests/organizations";
import { DefaultModal } from "../../../common/modals/default-modal/default-modal";
import { InputFormGroup } from "../../../common/input-form-group/input-form-group";
import { ButtonWithSpinner } from "../../../common/button-with-spinner/button-with-spinner";
import defaultStyles from "../../../common/modals/default-modal/default-modal.less";
import { BOOLEAN_RADIO_INPUT_OPTIONS } from "@constants/boolean-radio-input-options";
import { resources } from "@constants/hardcoded-strings";
import { LabelWidthTypes } from "@enums/label-width-types";
import { ButtonTypes } from "@enums/button-types";
import { LayoutTypes } from "@enums/layout-types";
import { RadioInputFormGroup } from "@containers/common/radio-input-form-group/radio-input-form-group";
import { Auth0IdentityProvider, Company } from "@idw-types/data/reference-data/generic";
import { configureCorporateLoginRequest } from "@requests/service-configuration";
import { createUserRequest } from "@requests/axnadmin-core";
import { AxnAdminUserPropertyBundle } from "@idw-types/data/axn-admin-user-property-bundle";
import { AxnAdminUserPropertyBundleTypes } from "@enums/axn-admin-user-property-bundle-types";
import { displaySuccessModal, logAndDisplayErrorModal } from "@utils/app-alert-util";
import { CustomerSupportTier } from "@idw-types/data/reference-data/generic/customer-support-tier";
import { SelectOption } from "@idw-types/common";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group";

type AddOrganizationModalProps = {
    auth0IdentityProviders: Array<Auth0IdentityProvider>;
    axnAdminUserPropertyBundles: Array<AxnAdminUserPropertyBundle>;
    customerSupportTiers: Array<CustomerSupportTier>;
    onModalClose: () => void;
};

type AddOrganizationModalState = {
    requestInProgress: boolean;
    organizationName: string;
    customerSupportTierUID: string;
    createFirstUser: boolean;
    firstName: string;
    lastName: string;
    emailAddress: string;
};

export const AddOrganizationModal = (props: AddOrganizationModalProps) => {
    const dispatch = useDispatch();
    const {
        auth0IdentityProviders,
        axnAdminUserPropertyBundles,
        customerSupportTiers,
        onModalClose
    } = props;

    const [labelWidth] = useState(LabelWidthTypes.MEDIUM);
    const [state, setState] = useState<AddOrganizationModalState>({
        requestInProgress: false,
        organizationName: "",
        customerSupportTierUID: null,
        createFirstUser: false,
        firstName: "",
        lastName: "",
        emailAddress: ""
    });
    const [attemptNumber, setAttemptNumber] = useState(0);
    const [hasCreatedOrganization, setHasCreatedOrganization] = useState(false);
    const [newOrganization, setNewOrganization] = useState<Company>(null);
    const [hasCreatedCorporateLogin, setHasCreatedCorporateLogin] = useState(false);
    const [hasCreatedFirstUser, setHasCreatedFirstUser] = useState(false);

    const createFirstUserForOrganization = async (organization: Company) => {
        const auth0IDPID = auth0IdentityProviders[0]?.id || null;

        const orgAdminPropertyBundle = axnAdminUserPropertyBundles.find(upb =>
            upb.type === AxnAdminUserPropertyBundleTypes.ORGANIZATION &&
            upb.shortName === "orgAdmin"
        ) || null;

        const newUser = await createUserRequest(
            state.firstName,
            state.lastName,
            state.emailAddress,
            state.emailAddress,
            "",
            "",
            organization.id,
            auth0IDPID,
            orgAdminPropertyBundle?.id,
            []
        );

        if (newUser?.data) {
            setHasCreatedFirstUser(true);

            displaySuccessModal(
                dispatch,
                resources.requests.createUser.success.title,
                resources.requests.createUser.success.message
            );

            onModalClose();
        } else {
            if (newUser?.error) {
                logAndDisplayErrorModal(
                    dispatch,
                    newUser.error,
                    resources.requests.createUser.error.title,
                    resources.requests.createUser.error.message,
                    newUser.error,
                    null,
                    newUser.body
                );
            }
            setState({ ...state, requestInProgress: false });
        }
    };

    const createCorporateLoginForOrganizationAndContinue = async (organization: Company) => {
        const newCorporateLogin = await configureCorporateLoginRequest(organization.id);
        if (newCorporateLogin?.data) {
            setHasCreatedCorporateLogin(true);

            displaySuccessModal(
                dispatch,
                resources.requests.configureCorporateLogin.success.title,
                resources.requests.configureCorporateLogin.success.message
            );

            if (state.createFirstUser) {
                await createFirstUserForOrganization(organization);
            } else {
                onModalClose();
            }
        } else {
            if (newCorporateLogin?.error) {
                logAndDisplayErrorModal(
                    dispatch,
                    newCorporateLogin.error,
                    resources.requests.configureCorporateLogin.error.title,
                    resources.requests.configureCorporateLogin.error.message,
                    null,
                    null,
                    newCorporateLogin.body
                );
            }

            setState({ ...state, requestInProgress: false });
        }
    };

    const createOrganizationAndContinue = async () => {
        const companyCode = nanoid(8);
        const newOrganization = await createOrganizationReduxRequest(dispatch)(state.organizationName, companyCode, state.customerSupportTierUID);
        if (newOrganization) {
            setHasCreatedOrganization(true);
            setNewOrganization(newOrganization);
            await createCorporateLoginForOrganizationAndContinue(newOrganization);
        } else {
            setState({ ...state, requestInProgress: false });
        }
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setState({ ...state, requestInProgress: true });
        if (!hasCreatedOrganization) {
            await createOrganizationAndContinue();
        }
        //this means 2nd step failed, retry it
        else if (!hasCreatedCorporateLogin && newOrganization) {
            await createCorporateLoginForOrganizationAndContinue(newOrganization);
        }
        //this means 3rd step failed - retry it
        else if (!hasCreatedFirstUser && hasCreatedCorporateLogin && newOrganization) {
            await createFirstUserForOrganization(newOrganization);
        }
        setAttemptNumber(attemptNumber + 1);
    };

    const handleUpdateOrganizationName = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            organizationName: event.target.value
        });
    };

    const handleUpdateCreateFirstUser = (value: string) => {
        setState({
            ...state,
            createFirstUser: isStringTrue(value),
            firstName: "",
            lastName: "",
            emailAddress: ""
        });
    };

    const handleUpdateFirstName = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            firstName: event.target.value
        });
    };

    const handleUpdateLastName = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            lastName: event.target.value
        });
    };

    const handleUpdateEmailAddress = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            emailAddress: event.target.value
        });
    };

    const handleCustomerSupportTierChange = (selectedOption: SelectOption<string>) => {
        setState({
            ...state,
            customerSupportTierUID: selectedOption.value
        });
    }

    const resource = resources.modals.addOrganization;

    const warningMessageText = useMemo(() => {
        if (attemptNumber > 0) {
            if (!hasCreatedOrganization) {
                return resource.warnings.retryCreateOrganization.label;
            } else if (!hasCreatedCorporateLogin) {
                return resource.warnings.retryConfigureLogin.label;
            } else if (state.createFirstUser && !hasCreatedFirstUser) {
                return resource.warnings.retryCreateUser.label;
            }
        }
        return null;
    }, [attemptNumber, hasCreatedOrganization]);

    const customerSupportTierOptions = useMemo(() => {
        return [...customerSupportTiers]
            .sort((a, b) => parseInt(a.id) - parseInt(b.id))
            .map(customerTier => ({ label: customerTier.name, value: customerTier.uid }))
    }, [customerSupportTiers]);

    const selectedCustomerTierOption = useMemo(() => {
        if (state.customerSupportTierUID) {
            return customerSupportTierOptions.find(customerTier => customerTier.value === state.customerSupportTierUID);
        }

        return null;
    }, [customerSupportTierOptions, state.customerSupportTierUID]);

    return (
        <DefaultModal
            automationId="add-organization-modal"
            ariaLabelledBy="add-organization-title"
            onHide={onModalClose}
            backdrop={"static"}
            keyboard={false}
            isMaxWidth={false}
        >
            <Form onSubmit={handleSubmit}>
                <Modal.Header id={"add-organization-title"} closeButton>
                    <Modal.Title>{resource.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <span className={defaultStyles.description}>
                        {resource.description}
                    </span>
                    <InputFormGroup
                        controlId="organization-name-input"
                        label={resource.inputs.organizationName.label}
                        ariaLabel={resource.inputs.organizationName.label}
                        automationId="organization-name-input"
                        placeholder={resource.inputs.organizationName.placeholder}
                        value={state.organizationName}
                        onChange={handleUpdateOrganizationName}
                        disabled={state.requestInProgress || hasCreatedOrganization}
                        isRequired={true}
                        labelWidth={labelWidth}
                    />

                    <SelectFormGroup
                        controlId={"custom-support-tier-select"}
                        automationId={"custom-support-tier-select"}
                        label={resource.inputs.customerSupportTier.label}
                        ariaLabel={resource.inputs.customerSupportTier.label}
                        options={customerSupportTierOptions}
                        selectedOption={selectedCustomerTierOption}
                        labelWidth={labelWidth}
                        isClearable={false}
                        isSearchable={false}
                        disabled={false}
                        isRequired={false}
                        onChange={handleCustomerSupportTierChange}
                    />

                    <RadioInputFormGroup
                        isRequired={true}
                        automationId={`create-organization-first-user-radio`}
                        controlId={`create-organization-first-user-radio`}
                        label={resource.inputs.createFirstUser.label}
                        onChange={handleUpdateCreateFirstUser}
                        value={`${state.createFirstUser}`}
                        disabled={state.requestInProgress}
                        options={BOOLEAN_RADIO_INPUT_OPTIONS}
                        labelWidth={labelWidth}
                        layout={LayoutTypes.ROW}
                        afterLabelTooltipText={resource.inputs.createFirstUser.tooltip}
                        radioButtonLayout={LayoutTypes.ROW}
                    />
                    {state.createFirstUser && (
                        <div>
                            <InputFormGroup
                                controlId="organization-first-user-first-name-input"
                                label={resource.inputs.firstName.label}
                                ariaLabel={resource.inputs.firstName.label}
                                automationId="organization-first-user-first-name-input"
                                placeholder={resource.inputs.firstName.placeholder}
                                value={state.firstName}
                                onChange={handleUpdateFirstName}
                                disabled={state.requestInProgress}
                                isRequired={true}
                                labelWidth={labelWidth}
                            />
                            <InputFormGroup
                                controlId="organization-first-user-last-name-input"
                                label={resource.inputs.lastName.label}
                                ariaLabel={resource.inputs.lastName.label}
                                automationId="organization-first-user-last-name-input"
                                placeholder={resource.inputs.lastName.placeholder}
                                value={state.lastName}
                                onChange={handleUpdateLastName}
                                disabled={state.requestInProgress}
                                isRequired={true}
                                labelWidth={labelWidth}
                            />
                            <InputFormGroup
                                controlId="organization-first-user-email-address-input"
                                label={resource.inputs.email.label}
                                ariaLabel={resource.inputs.email.label}
                                automationId="organization-first-user-email-address-input"
                                placeholder={resource.inputs.email.placeholder}
                                value={state.emailAddress}
                                type={"email"}
                                onChange={handleUpdateEmailAddress}
                                disabled={state.requestInProgress}
                                isRequired={true}
                                labelWidth={labelWidth}
                            />
                        </div>
                    )}
                    {warningMessageText && (
                        <Alert variant={"warning"}>{warningMessageText}</Alert>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button id="add-organization-cancel-button" variant="secondary" onClick={onModalClose}>
                        {resources.common.buttons.cancel.label}
                    </Button>
                    <ButtonWithSpinner
                        id="add-organization-submit-button"
                        disabled={state.requestInProgress}
                        hideSpinner={false}
                        variant="primary"
                        type={ButtonTypes.SUBMIT}
                        label={resources.common.buttons.save.label}
                    />
                </Modal.Footer>
            </Form>
        </DefaultModal>
    );
};